import React, { useEffect, useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown, faRotate } from "@fortawesome/free-solid-svg-icons";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./App.css";
import VideoCard from "./components/VideoCard";
import BottomNavbar from "./components/BottomNavbar";
import TopNavbar from "./components/TopNavbar";
import DonationsPage from "./components/DonationsPage";
import { getVideoUrl, refreshNewVideos, getVideoMetadataFromRedis } from "./appwrite/appwrite";

function ShortVideoMain() {
  const [videos, setVideos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const videoRefs = useRef({});
  const [isGlobalMuted, setIsGlobalMuted] = useState(true);
  const [globalVolume, setGlobalVolume] = useState(1);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const VIDEOS_PER_PAGE = 5;
  const PRELOAD_THRESHOLD = 3;
  const PRELOAD_VIDEO_COUNT = 2;

  const loadVideos = useCallback(async (pageNum) => {
    if (!hasMore || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const metadata = await getVideoMetadataFromRedis(pageNum, VIDEOS_PER_PAGE);

      if (metadata.length === 0) {
        setHasMore(false);
        return;
      }

      const shuffledNewVideos = [...metadata]
        .map((value) => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);

      setVideos((prevVideos) => [...prevVideos, ...shuffledNewVideos]);
      setPage(pageNum + 1);
    } catch (error) {
      console.error("Error fetching video metadata:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore]);

  useEffect(() => {
    const initialLoad = async () => {
      await loadVideos(1);
      if (hasMore) {
        await loadVideos(2);
      }
    };
    initialLoad();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const videoHeight = container.clientHeight;
      const scrollPosition = container.scrollTop;
      const newIndex = Math.round(scrollPosition / videoHeight);

      if (
        newIndex !== currentVideoIndex &&
        newIndex >= 0 &&
        newIndex < videos.length
      ) {
        setCurrentVideoIndex(newIndex);

        if (
          videos.length - newIndex <= PRELOAD_THRESHOLD &&
          hasMore &&
          !isLoadingMore
        ) {
          loadVideos(page);
        }

        preloadUpcomingVideos(newIndex);
        cleanupFarVideos(newIndex);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [
    videos.length,
    currentVideoIndex,
    page,
    hasMore,
    isLoadingMore,
    loadVideos,
  ]);

  const preloadUpcomingVideos = (currentIndex) => {
    for (
      let i = currentIndex + 1;
      i <= currentIndex + PRELOAD_VIDEO_COUNT;
      i++
    ) {
      if (i < videos.length && videoRefs.current[i]) {
        const videoRef = videoRefs.current[i];

        if (videoRef.dataset.src && !videoRef.src) {
          videoRef.src = videoRef.dataset.src;
          videoRef.load();
        }
      }
    }
  };

  const cleanupFarVideos = (currentIndex) => {
    const CLEANUP_THRESHOLD = 15;

    Object.keys(videoRefs.current).forEach((index) => {
      const videoIndex = parseInt(index);
      const videoRef = videoRefs.current[index];

      if (
        videoRef &&
        Math.abs(videoIndex - currentIndex) > CLEANUP_THRESHOLD &&
        (videoIndex < currentIndex - CLEANUP_THRESHOLD ||
          videoIndex > currentIndex + PRELOAD_VIDEO_COUNT + CLEANUP_THRESHOLD)
      ) {
        const videoUrl = videoRef.src;
        if (videoUrl && !videoUrl.includes("blob:")) {
          videoRef.removeAttribute("src");
          videoRef.load();
          videoRef.dataset.src = videoUrl;
        }
      }
    });
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const videoElement = entry.target;
        const videoIndex = parseInt(videoElement.dataset.index);

        if (entry.isIntersecting) {
          if (videoElement.dataset.src && !videoElement.src) {
            videoElement.src = videoElement.dataset.src;
          }

          videoElement.muted = isGlobalMuted;
          videoElement.volume = globalVolume;
          if (Math.abs(videoIndex - currentVideoIndex) <= 1) {
            videoElement.play().catch((error) => {
              console.log("Video play failed:", error);
            });
          } else if (
            Math.abs(videoIndex - currentVideoIndex) <= PRELOAD_VIDEO_COUNT
          ) {
            videoElement.load();
          }
        } else {
          videoElement.pause();
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions
    );

    Object.values(videoRefs.current).forEach((videoRef) => {
      if (videoRef) {
        observer.observe(videoRef);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [videos, isGlobalMuted, globalVolume, currentVideoIndex]);

  const handleVideoRef = (index) => (ref) => {
    if (ref) {
      videoRefs.current[index] = ref;
      ref.dataset.index = index;

      if (
        index > currentVideoIndex &&
        index <= currentVideoIndex + PRELOAD_VIDEO_COUNT
      ) {
        if (ref.dataset.src && !ref.src) {
          ref.src = ref.dataset.src;
          ref.load();
        }
      }
    }
  };

  const handleMuteToggle = () => {
    setIsGlobalMuted(!isGlobalMuted);
    Object.values(videoRefs.current).forEach((videoRef) => {
      if (videoRef) {
        videoRef.muted = !isGlobalMuted;
      }
    });
  };

  const handleGlobalVolumeChange = (newVolume) => {
    setGlobalVolume(newVolume);
    Object.values(videoRefs.current).forEach((videoRef) => {
      if (videoRef) {
        videoRef.volume = newVolume;
        if (newVolume === 0 && !isGlobalMuted) {
          setIsGlobalMuted(true);
        } else if (newVolume > 0 && isGlobalMuted) {
          setIsGlobalMuted(false);
        }
      }
    });
  };

  const scrollToVideo = (direction) => {
    const container = containerRef.current;
    if (!container) return;

    const videoHeight = container.clientHeight;
    const newIndex =
      direction === "up"
        ? Math.max(currentVideoIndex - 1, 0)
        : Math.min(currentVideoIndex + 1, videos.length - 1);

    if (newIndex >= 0 && newIndex < videos.length) {
      setCurrentVideoIndex(newIndex);
      container.scrollTo({
        top: videoHeight * newIndex,
        behavior: "smooth",
      });
    }
  };

  const handleRefreshVideos = async () => {
    try {
      setIsLoading(true);
      const response = await refreshNewVideos(VIDEOS_PER_PAGE);
      if (response) {
        setVideos([]);
        setPage(1);
        setHasMore(true);

        await loadVideos(1);
        if (hasMore) {
          await loadVideos(2);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error refreshing videos:", error);
      alert("Failed to refresh videos. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="container" ref={containerRef}>
        <TopNavbar className="top-navbar" />
        <div className="refresh-button-container">
          <button
            className="refresh-button"
            onClick={handleRefreshVideos}
            disabled={isLoading}
            aria-label="Refresh videos"
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={isLoading ? "fa-spin" : ""}
            />
          </button>
          <div className="refresh-tooltip">
            Get new & fresh engaging videos &#128008;
          </div>
        </div>
        {videos.map((video, index) => (
          <VideoCard
            key={`video-${index}`}
            username={video.username}
            description={video.description}
            song={video.song}
            likes={video.likes}
            saves={video.saves}
            comments={video.comments}
            shares={video.shares}
            url={""}
            profilePic={video.profilePic}
            setVideoRef={handleVideoRef(index)}
            isMuted={isGlobalMuted}
            onMuteToggle={handleMuteToggle}
            volume={globalVolume}
            onVolumeChange={handleGlobalVolumeChange}
            data-src={getVideoUrl(video.fileId)}
            shouldPreload={index <= currentVideoIndex + PRELOAD_VIDEO_COUNT}
          />
        ))}
        {isLoadingMore && (
          <div className="loading-indicator">
            Loading more videos... &#128525;
          </div>
        )}
        {!hasMore && videos.length > 0 && (
          <div className="end-of-feed">You've reached the end!</div>
        )}
        <BottomNavbar className="bottom-navbar" />
      </div>

      <div className="nav-buttons">
        <button className="nav-button" onClick={() => scrollToVideo("up")}>
          <FontAwesomeIcon icon={faChevronUp} />
        </button>
        <button className="nav-button" onClick={() => scrollToVideo("down")}>
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<ShortVideoMain />} />
        <Route path="/donations" element={<DonationsPage />} />
        <Route path="*" element={<ShortVideoMain />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
